import React, { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import AppContext from "../../contexts/AppContext";
import examService from "../../services/ExamService";
import { getBodyRegionNames } from "../../utils/Utils";
import {
    canJoinRemoteAssistance,
    getRemoteAssistanceStatus,
    REMOTE_ASSISTANCE_STATUS_DETAILS
} from "./remoteAssistanceUtils";
import "./RemoteAssistanceSection.scss";

const SUPPORT_EMAIL = "support@atlasathlete.ai";

const formatMeetingDate = (value) => {
    if (!value) return "Schedule unavailable";

    const date = new Date(value.endsWith?.("Z") ? value : `${value}Z`);
    if (Number.isNaN(date.getTime())) return "Schedule unavailable";

    return new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short"
    }).format(date);
};

const StatusMessage = ({ message }) => {
    if (!message) return null;

    const [prefix, suffix] = message.split(SUPPORT_EMAIL);
    return (
        <p className="remote-assistance-card__message">
            {prefix}
            {suffix !== undefined && <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>}
            {suffix}
        </p>
    );
};

const RemoteAssistanceSection = () => {
    const { setTelemedicineProperties, resetTelemedicineKeys } = useContext(AppContext);
    const [remoteAssistances, setRemoteAssistances] = useState([]);
    const [joiningInvitationId, setJoiningInvitationId] = useState(null);
    const [currentTime, setCurrentTime] = useState(() => new Date());

    useEffect(() => {
        let isMounted = true;

        const fetchRemoteAssistances = async () => {
            const subscriptionType = parseInt(localStorage.getItem("SubscriptionType"), 10);
            const response = await examService.getAssessments({
                PageSize: 1000,
                PageNumber: 1,
                ReportStatus: null,
                BodyRegionList: [],
                SportsType: [],
                IsFollowUp: null,
                IsInCompleted: 0,
                Completed: null,
                Search: "",
                SubscriptionType: subscriptionType
            });

            if (!isMounted) return;

            if (!response.success) {
                console.error(response.error);
                return;
            }

            const records = Array.isArray(response.data?.MyRomList) ? response.data.MyRomList : [];
            setRemoteAssistances(records.filter((record) => record?.IsRemoteAssistant === true));
        };

        fetchRemoteAssistances();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const timer = window.setInterval(() => setCurrentTime(new Date()), 60 * 1000);
        return () => window.clearInterval(timer);
    }, []);

    const items = useMemo(() => remoteAssistances.map((record) => {
        const status = getRemoteAssistanceStatus(record, currentTime);
        return { record, status, details: REMOTE_ASSISTANCE_STATUS_DETAILS[status] };
    }), [remoteAssistances, currentTime]);

    if (!items.length) return null;

    const joinRoom = (record) => {
        if (!record?.InvitationId) {
            toast.error("Meeting information is unavailable. Please contact support.");
            return;
        }

        setJoiningInvitationId(record.InvitationId);
        setTelemedicineProperties(
            record.InvitationId,
            record.BodyRegionList || [],
            null,
            () => {
                resetTelemedicineKeys();
                setJoiningInvitationId(null);
            }
        );
    };

    return (
        <section className="remote-assistance" aria-labelledby="remote-assistance-title">
            <div className="remote-assistance__heading">
                <div>
                    <span className="remote-assistance__eyebrow">Live guided assessment</span>
                    <h2 id="remote-assistance-title">Remote Assistance</h2>
                </div>
                <span className="remote-assistance__count">{items.length}</span>
            </div>

            <div className="remote-assistance__list">
                {items.map(({ record, status, details }, index) => {
                    const canJoin = canJoinRemoteAssistance(status);
                    const startDate = record.MeetingStartDate ?? record.MeetingStart;
                    const endDate = record.MeetingEndDate ?? record.MeetingEnd;
                    const invitationId = record.InvitationId;

                    return (
                        <article className="remote-assistance-card" key={record.TestId || invitationId || index}>
                            <div className="remote-assistance-card__content">
                                <div className="remote-assistance-card__topline">
                                    <span className={`remote-assistance-card__status remote-assistance-card__status--${status}`}>
                                        {details.label}
                                    </span>
                                    {record.TestId && <span className="remote-assistance-card__test-id">Test ID {record.TestId}</span>}
                                </div>

                                <div className="remote-assistance-card__schedule">
                                    <div>
                                        <span>Starts</span>
                                        <strong>{formatMeetingDate(startDate)}</strong>
                                    </div>
                                    <div>
                                        <span>Ends</span>
                                        <strong>{formatMeetingDate(endDate)}</strong>
                                    </div>
                                    {!!record.BodyRegionList?.length && (
                                        <div>
                                            <span>Region</span>
                                            <strong>{getBodyRegionNames(record.BodyRegionList)}</strong>
                                        </div>
                                    )}
                                </div>

                                <div className="remote-assistance-card__footer">
                                    <StatusMessage message={details.message} />
                                    {canJoin && (
                                        <button
                                            type="button"
                                            className="remote-assistance-card__join"
                                            disabled={joiningInvitationId === invitationId}
                                            onClick={() => joinRoom(record)}
                                        >
                                            {joiningInvitationId === invitationId && <i className="fa fa-spinner fa-spin" aria-hidden="true" />}
                                            Join Telemedicine
                                        </button>
                                    )}
                                </div>
                            </div>
                        </article>
                    );
                })}
            </div>
        </section>
    );
};

export default RemoteAssistanceSection;
